import React, { ReactElement, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { AxiosError, AxiosResponse } from 'axios';
import styled from 'styled-components';

import { RegisterApiResponse } from 'features/auth';
import { Page, Box, Typography, Flex, HelperText, Loading, Label, Checkbox, Radio } from 'components';
import { request, createUrl, useFormikHandles, phoneMask, cpfMask, URLParams } from 'app/utils';
import { useSchool } from 'features/school';
import Alert, { useAlert } from 'features/alert';
import { AuthInput2, AuthButton } from 'features/auth/components';
import usestorage from 'app/utils/storage';
import { cpf } from 'cpf-cnpj-validator';
import { CustomersTypes } from 'features/account';
import { useResources } from 'features/resources';

const ExternalLink = styled.a`
  color: ${({ theme }) => theme.colors.gray_500};
  text-decoration: underline;
`;

const customMaskPhone = (phone: string, isInternational: boolean) => {
  return isInternational ? phone : phoneMask(phone.replace('+55', '').trim()).substring(0, 15);
};

const RegisterView = (): ReactElement => {
  const navigate = useNavigate();
  const { apiGatewayUrl, schoolAlias } = useSchool();
  const { setAlert, clearAlert } = useAlert();
  const { termsLink } = useResources();

  const storage = usestorage('sessionStorage');

  const [searchParams] = useSearchParams();
  const src = searchParams.get('src') || '';

  const initialValues: Record<string, any> = {
    name: '',
    email: '',
    isInternational: false,
    showPhone: 'y',
    phone: '',
    showTaxId: 'n',
    taxId: '',
    terms: false,
    showProfileType: 'n',
    profileType: '',
  };

  const phoneRegExp = /^\([1-9]{2}\) [2-9][0-9]{3,4}-[0-9]{4}/;
  // const taxIdRegExp = /(^\d{3}\.\d{3}\.\d{3}-\d{2}$)|(^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$)/;

  const formik = useFormik({
    initialValues,
    validationSchema: Yup.object({
      name: Yup.string().required('Você precisa informar seu nome.'),

      email: Yup.string().email('O e-mail informado está inválido.').required('Você precisa informar seu e-mail.'),

      taxId: Yup.string().when('showTaxId', {
        is: 'y',
        then: Yup.string()
          .required('Você precisa informar seu CPF.')
          .test(function (value, { createError, path }) {
            return (
              cpf.isValid(`${value}`) ||
              createError({
                path,
                message: 'O CPF informado está inválido.',
              })
            );
          }),
      }),

      phone: Yup.string().when('showPhone', {
        is: 'y',
        then: Yup.string()
          .when('isInternational', {
            is: false,
            then: Yup.string().matches(phoneRegExp, `O telefone informado está inválido.`),
          })
          .required('Você precisa informar seu telefone.'),
      }),

      terms: Yup.boolean().oneOf([true], 'Você precisa ler e aceitar os termos de uso e privacidade da plataforma'),

      profileType: Yup.string().when('showProfileType', {
        is: 'y',
        then: Yup.string().required('Informe o tipo de conteúdo de sua preferência'),
      }),
    }),
    onSubmit: (values, { setSubmitting }) => {
      const { name, email, showPhone, phone, showTaxId, taxId, showProfileType, profileType } = values;

      const enrollSlug = storage.get('enrollSlug');

      const params = URLParams({
        regCode: enrollSlug || undefined,
        src,
      });

      request(
        {
          url: `/api/v1/school/registration/${schoolAlias}?${params}`,
          method: 'post',
          baseURL: apiGatewayUrl,
          data: {
            name,
            contactEmail: email,
            contactPhone: showPhone ? phone : undefined,
            internationalContactPhone: showPhone ? Boolean(values.isInternational) : undefined,
            taxId: showTaxId ? taxId.replace(/[^\d]/g, '') : undefined,
            customerType: CustomersTypes.Person,
            profileType: showProfileType ? profileType : undefined,
          },
        },
        true
      )
        .then((response: AxiosResponse<RegisterApiResponse>) => {
          setSubmitting(false);

          if (enrollSlug) {
            const enrollProductPrefix = storage.get('enrollProductPrefix');
            storage.set('redirect_url', `/${enrollProductPrefix || 'products'}/${enrollSlug}`);

            storage.remove('enrollSlug');
            storage.remove('enrollProductPrefix');
          }

          navigate(createUrl('registerSuccess'));
        })
        .catch((error: AxiosError<RegisterApiResponse>) => {
          setSubmitting(false);

          if (error.response && error.response.data) {
            const { apiResultMessage } = error.response.data;

            setAlert({
              type: 'error',
              message: apiResultMessage,
            });

            // const { apiResultCode } = error.response.data;

            // if (apiResultCode === 2) {
            //   setAlert({
            //     type: 'error',
            //     message: `Ja existe um cadastro com o e-mail <strong>${email}</strong>. <a href="${createUrl(
            //       'forgotPassword'
            //     )}">Clique aqui</a> para redefinir sua senha.`,
            //   });
            // } else {
            //   setAlert({
            //     type: 'error',
            //     message: 'Ocorreu um erro inesperado. Por favor, tente novamente.',
            //   });
            // }
          } else {
            setAlert({
              type: 'error',
              message: 'Ocorreu um erro inesperado. Por favor, tente novamente.',
            });
          }
        });
    },
  });

  const { formikHandleFocus, formikHandleBlur, formikHandleChange } = useFormikHandles(formik);

  useEffect(() => {
    if (schoolAlias === 'b3') {
      formik.setFieldValue('showPhone', 'n');
      formik.setFieldValue('showTaxId', 'y');
      formik.setFieldValue('showProfileType', 'y');
    }
  }, [schoolAlias]);

  useEffect(() => () => clearAlert(), []); // Clear Alert

  return (
    <Page title="Register">
      {formik.isSubmitting && <Loading alpha />}

      <Alert />

      <form onSubmit={formik.handleSubmit}>
        <Flex flexDirection="column">
          <Flex flexGrow={1} flexDirection="column" px={{ m: 30, d: 45 }} py={{ m: 30, d: 45 }}>
            <Typography as="h1" type="type6" color="authLabelColor" fontWeight="bold">
              Cadastro
            </Typography>

            <Box mt={2}>
              <Typography lineHeight="1.2" type="type2" color="authLabelColor">
                Preencha os campos abaixo para criar sua conta.
              </Typography>
            </Box>

            {/* name */}
            <Flex flexDirection="column" mt={{ m: 4 }}>
              <label htmlFor="login-name">
                <Typography type="type2" color="authLabelColor" upper fontWeight="bold">
                  Nome completo:
                </Typography>
              </label>

              <Box position="relative" mt={2}>
                <AuthInput2
                  id="login-name"
                  name="name"
                  type="text"
                  onChange={formikHandleChange}
                  onFocus={formikHandleFocus}
                  onBlur={formikHandleBlur}
                  value={formik.values.name}
                  data-haserror={formik.touched.name && formik.errors.name}
                  data-testid="auth-login-name"
                />
              </Box>

              {formik.touched.name && formik.errors.name ? <HelperText type="error">{formik.errors.name}</HelperText> : null}
            </Flex>
            {/* /name */}

            {/* email */}
            <Flex flexDirection="column" mt={{ m: 20, d: 30 }}>
              <label htmlFor="login-email">
                <Typography type="type2" color="authLabelColor" upper fontWeight="bold">
                  E-mail:
                </Typography>
              </label>

              <Box position="relative" mt={2}>
                <AuthInput2
                  id="login-email"
                  name="email"
                  type="text"
                  onChange={formikHandleChange}
                  onFocus={formikHandleFocus}
                  onBlur={formikHandleBlur}
                  value={formik.values.email}
                  data-haserror={formik.touched.email && formik.errors.email}
                  data-testid="auth-login-email"
                />
              </Box>

              {formik.touched.email && formik.errors.email ? <HelperText type="error">{formik.errors.email}</HelperText> : null}
            </Flex>
            {/* /email */}

            {/* taxId */}
            {formik.values.showTaxId === 'y' && (
              <Flex flexDirection="column" mt={{ m: 20, d: 30 }}>
                <label htmlFor="login-cpf">
                  <Typography type="type2" color="authLabelColor" upper fontWeight="bold">
                    CPF:
                  </Typography>
                </label>

                <Box position="relative" mt={2}>
                  <AuthInput2
                    id="login-cpf"
                    name="taxId"
                    type="text"
                    onChange={formikHandleChange}
                    onFocus={formikHandleFocus}
                    onBlur={formikHandleBlur}
                    value={cpfMask(formik.values.taxId)}
                    data-haserror={formik.touched.taxId && formik.errors.taxId}
                    data-testid="auth-login-taxId"
                  />
                </Box>

                {formik.touched.taxId && formik.errors.taxId ? <HelperText type="error">{formik.errors.taxId}</HelperText> : null}
              </Flex>
            )}
            {/* /taxId */}

            {/* phone */}
            {formik.values.showPhone === 'y' && (
              <Flex flexDirection="column" mt={{ m: 20, d: 30 }}>
                <Flex flexGrow={1} alignItems="center" justifyContent="space-between">
                  <label htmlFor="login-phone">
                    <Typography type="type2" color="authLabelColor" upper fontWeight="bold">
                      Telefone
                      {!formik.values.isInternational && (
                        <>
                          {' '}
                          <Typography as="span" type="type2" color="authLabelColor" fontWeight="light">
                            (com DDD)
                          </Typography>
                        </>
                      )}
                      :
                    </Typography>
                  </label>

                  <Flex flexGrow={0} alignItems="center" flexWrap="nowrap">
                    <Checkbox name="isInternational" checked={formik.values.isInternational} onChange={formikHandleChange} />

                    <Typography
                      as="span"
                      type="type2"
                      color="authLabelColor"
                      fontWeight="light"
                      ml={2}
                      onClick={() => {
                        formik.setFieldValue('isInternational', !formik.values.isInternational);
                      }}
                    >
                      Internacional
                    </Typography>
                  </Flex>
                </Flex>

                <Box position="relative" mt={2}>
                  <AuthInput2
                    id="login-phone"
                    name="phone"
                    type="text"
                    onChange={(e) => {
                      if (formik.values.isInternational) {
                        if (!/[a-zA-Z]/g.test(e.currentTarget.value)) {
                          formikHandleChange(e);
                        }
                      } else {
                        formikHandleChange(e);
                      }
                    }}
                    onFocus={formikHandleFocus}
                    onBlur={formikHandleBlur}
                    value={customMaskPhone(formik.values.phone, formik.values.isInternational)}
                    data-haserror={formik.touched.phone && formik.errors.phone}
                    data-testid="auth-login-phone"
                    maxLength={formik.values.isInternational ? undefined : 15}
                  />
                </Box>

                {formik.touched.phone && formik.errors.phone ? <HelperText type="error">{formik.errors.phone}</HelperText> : null}
              </Flex>
            )}
            {/* /phone */}

            {/* profileType */}
            {formik.values.showProfileType === 'y' && (
              <Flex flexDirection="column" mt={{ m: 20, d: 30 }}>
                <label htmlFor="login-phone">
                  <Typography type="type2" color="authLabelColor" upper fontWeight="bold">
                    Você prefere ver cursos para:
                  </Typography>
                </label>

                <Flex flexGrow={0} style={{ gap: 20 }}>
                  <Flex flexGrow={0} position="relative" mt={2} alignItems="flex-start" flexWrap="nowrap" style={{ cursor: 'pointer' }}>
                    <Radio
                      color="primary_contrast"
                      checked={formik.values.profileType === 'Investidor'}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        formik.setFieldValue('profileType', 'Investidor');
                      }}
                    />

                    <Typography
                      color="authLabelColor"
                      ml={2}
                      pt="1px"
                      onClick={() => {
                        formik.setFieldValue('profileType', 'Investidor');
                      }}
                    >
                      Investidor
                    </Typography>
                  </Flex>

                  <Flex flexGrow={0} position="relative" mt={2} alignItems="flex-start" flexWrap="nowrap" style={{ cursor: 'pointer' }}>
                    <Radio
                      color="primary_contrast"
                      checked={formik.values.profileType === 'Profissional'}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        formik.setFieldValue('profileType', 'Profissional');
                      }}
                    />

                    <Typography
                      color="authLabelColor"
                      ml={2}
                      pt="1px"
                      onClick={() => {
                        formik.setFieldValue('profileType', 'Profissional');
                      }}
                    >
                      Profissional
                    </Typography>
                  </Flex>
                </Flex>

                {formik.touched.profileType && formik.errors.profileType ? <HelperText type="error">{formik.errors.profileType}</HelperText> : null}
              </Flex>
            )}
            {/* /profileType */}

            {/* terms */}
            <Flex flexDirection="column" mt={{ m: 20, d: 30 }}>
              <Flex position="relative" mt={2} alignItems="flex-start" flexWrap="nowrap">
                <Checkbox
                  color="primary_contrast"
                  checked={formik.values.terms}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    formik.setFieldValue('terms', event.currentTarget.checked);
                  }}
                />

                <Typography
                  color="authLabelColor"
                  ml={2}
                  pt="1px"
                  onClick={() => {
                    formik.setFieldValue('terms', !formik.values.terms);
                  }}
                >
                  <Label htmlFor="register-terms">
                    Declaro que li e aceito os{' '}
                    <ExternalLink href={termsLink} target="_blank" rel="noreferrer">
                      termos de uso e privacidade
                    </ExternalLink>{' '}
                    da plataforma
                  </Label>
                </Typography>
              </Flex>

              {formik.touched.terms && formik.errors.terms ? <HelperText type="error">{formik.errors.terms}</HelperText> : null}
            </Flex>
            {/* /terms */}
          </Flex>

          <Flex>
            <AuthButton type="submit" data-testid="auth-login-submit" className="btn-submit-register">
              Cadastrar
            </AuthButton>
          </Flex>

          <Flex bg="authBackground" justifyContent="center" py={3}>
            <Link to={createUrl('login')}>
              <Typography className="btn-auth-external" color="authFloatLinkColor">
                Já tenho cadastro
              </Typography>
            </Link>
          </Flex>
        </Flex>
      </form>
    </Page>
  );
};

export default RegisterView;
